import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { LogOut, User, Building2, ChevronDown, Check } from 'lucide-react'
import type { Actor } from '../../hooks/useActors'

interface HeaderProps {
  userName?: string
  onLogout?: () => void
  actors?: Actor[]
  activeActorId?: string
  onActorChange?: (actorId: string) => void
}

/**
 * Task pane header with account switcher (replaces logo), user avatar, and logout.
 * Account picker is shown where "sign by kobsign" used to be.
 */
export function Header({ userName, onLogout, actors, activeActorId, onActorChange }: HeaderProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const activeActor = actors?.find((a) => a.koblink_id === activeActorId)
  const hasMultipleActors = actors && actors.length > 1
  const isOrg = activeActor?.actor_type === 'ORGANIZATION'
  const ActorIcon = isOrg ? Building2 : User

  return (
    <header ref={ref} className="relative border-b border-neutral-200/80 dark:border-neutral-700 bg-gradient-to-r from-primary-50/40 via-surface to-surface dark:from-primary-900/10 dark:via-[rgb(var(--color-surface))] dark:to-[rgb(var(--color-surface))]">
      <div className="flex items-center justify-between px-2.5 py-1.5">
        {/* Left: Account picker (or logo fallback when not logged in) */}
        <div className="flex-1 min-w-0">
          {activeActor ? (
            <button
              onClick={() => hasMultipleActors && setIsOpen(!isOpen)}
              className={`flex items-center gap-1.5 min-w-0 ${
                hasMultipleActors ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/40 ring-1 ring-primary-200/50 dark:ring-primary-800/50 shrink-0">
                <ActorIcon size={11} className="text-primary-600 dark:text-primary-400" />
              </div>
              <span className="text-[11px] font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                {activeActor.display_name}
              </span>
              {hasMultipleActors && (
                <ChevronDown
                  size={12}
                  className={`shrink-0 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              )}
            </button>
          ) : userName ? (
            /* Logged in but actors still loading — show placeholder */
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="w-5 h-5 rounded-full bg-neutral-100 dark:bg-neutral-700 animate-pulse shrink-0" />
              <div className="h-3 w-20 bg-neutral-100 dark:bg-neutral-700 rounded animate-pulse" />
            </div>
          ) : (
            /* Fallback logo when not logged in */
            <div className="flex items-baseline gap-1">
              <span className="text-sm font-extrabold tracking-tight text-primary-600 dark:text-primary-400">
                sign
              </span>
              <span className="text-[9px] text-neutral-400 dark:text-neutral-500">
                by kobsign
              </span>
            </div>
          )}
        </div>

        {/* Right: User + logout */}
        {userName && (
          <div className="flex items-center gap-1 shrink-0 ml-2">
            <span className="text-[10px] text-neutral-500 dark:text-neutral-400 truncate max-w-[70px]">
              {userName}
            </span>
            {onLogout && (
              <button
                onClick={onLogout}
                className="p-0.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                title={t('auth.logoutButton')}
              >
                <LogOut size={12} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Account dropdown */}
      {isOpen && actors && (
        <div className="absolute left-0 right-0 top-full bg-white dark:bg-neutral-800 border rounded-b-lg border-neutral-200 dark:border-neutral-700 shadow-xl animate-slide-down z-50">
          <div className="px-3 py-1.5 text-[10px] font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
            {t('accountBar.switchAccount')}
          </div>
          {actors.map((actor) => {
            const Icon = actor.actor_type === 'ORGANIZATION' ? Building2 : User
            const isActive = actor.koblink_id === activeActorId

            return (
              <button
                key={actor.koblink_id}
                onClick={() => {
                  onActorChange?.(actor.koblink_id)
                  setIsOpen(false)
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20'
                    : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                }`}
              >
                <div className={`flex items-center justify-center w-6 h-6 rounded-full ${
                  isActive
                    ? 'bg-primary-100 dark:bg-primary-900/40'
                    : 'bg-neutral-100 dark:bg-neutral-700'
                }`}>
                  <Icon size={13} className={
                    isActive
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-neutral-400 dark:text-neutral-500'
                  } />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-neutral-900 dark:text-neutral-100 truncate">
                    {actor.display_name}
                  </p>
                  <p className="text-[10px] text-neutral-400 dark:text-neutral-500">
                    {actor.actor_type === 'ORGANIZATION'
                      ? t('accountBar.organization')
                      : t('accountBar.personal')}
                    {actor.role && ` · ${actor.role}`}
                  </p>
                </div>
                {isActive && (
                  <Check size={14} className="shrink-0 text-primary-500" />
                )}
              </button>
            )
          })}
        </div>
      )}
    </header>
  )
}
